import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { audioManager } from '../../utils/AudioManager';
import { buttonHover, buttonTap } from '../../utils/AnimationPresets';
import { useTheme } from '../../context/ThemeContext';

const AudioControls: React.FC = () => {
    const [isMuted, setIsMuted] = useState(audioManager.muted);
    const [showSettings, setShowSettings] = useState(false);
    const [musicVolume, setMusicVolume] = useState(audioManager.currentMusicVolume);
    const [sfxVolume, setSFXVolume] = useState(audioManager.currentSFXVolume);
    const { currentTheme, setTheme, availableThemes } = useTheme();

    const handleToggleMute = () => {
        const newMuted = audioManager.toggleMute();
        setIsMuted(newMuted);
    };

    const handleMusicVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const volume = parseFloat(e.target.value);
        setMusicVolume(volume);
        audioManager.setMusicVolume(volume);
    };

    const handleSFXVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const volume = parseFloat(e.target.value);
        setSFXVolume(volume);
        audioManager.setSFXVolume(volume);
        // Jouer un SFX de test
        audioManager.playSFX('click');
    };

    return (
        <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
            {/* Bouton Mute/Unmute */}
            <motion.button
                onClick={handleToggleMute}
                className="p-3 bg-black/80 border border-gold-500/30 rounded-lg hover:bg-gold-500/10 hover:border-gold-500/50 transition-all shadow-lg backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={isMuted ? 'Activer le son' : 'Couper le son'}
            >
                {isMuted ? (
                    <VolumeX className="w-5 h-5 text-gold-400" />
                ) : (
                    <Volume2 className="w-5 h-5 text-gold-400" />
                )}
            </motion.button>

            {/* Bouton Paramètres Audio */}
            <motion.button
                onClick={() => setShowSettings(!showSettings)}
                className="p-3 bg-black/80 border border-gold-500/30 rounded-lg hover:bg-gold-500/10 hover:border-gold-500/50 transition-all shadow-lg backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Paramètres audio"
            >
                <Settings className="w-5 h-5 text-gold-400" />
            </motion.button>

            {/* Panel Paramètres */}
            <AnimatePresence>
                {showSettings && (
                    <motion.div
                        initial={{ opacity: 0, x: -20, y: 10 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        exit={{ opacity: 0, x: -20, y: 10 }}
                        className="absolute bottom-full left-0 mb-2 p-4 bg-black/90 border border-gold-500/30 rounded-lg shadow-[0_0_20px_rgba(234,179,8,0.2)] backdrop-blur-md min-w-[280px]"
                    >
                        <h3 className="text-gold-400 font-bold mb-4 font-serif border-b border-gold-500/20 pb-2">Paramètres</h3>

                        {/* Thème */}
                        <div className="mb-4">
                            <label className="text-gold-100/80 text-sm block mb-2 font-serif">
                                Thème Visuel
                            </label>
                            <select
                                value={currentTheme._id}
                                onChange={(e) => setTheme(e.target.value)}
                                className="w-full px-3 py-2 bg-neutral-900 border border-gold-500/30 rounded text-gold-100 text-sm focus:outline-none focus:border-gold-500/50 cursor-pointer font-serif"
                            >
                                {availableThemes.map(theme => (
                                    <option key={theme._id} value={theme._id}>
                                        {theme.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Volume Musique */}
                        <div className="mb-4">
                            <label className="text-gold-100/80 text-sm block mb-2 font-serif">
                                Musique : {Math.round(musicVolume * 100)}%
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={musicVolume}
                                onChange={handleMusicVolumeChange}
                                className="w-full h-2 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-gold-500"
                            />
                        </div>

                        {/* Volume SFX */}
                        <div>
                            <label className="text-gold-100/80 text-sm block mb-2 font-serif">
                                Effets Sonores : {Math.round(sfxVolume * 100)}%
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={sfxVolume}
                                onChange={handleSFXVolumeChange}
                                className="w-full h-2 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-gold-500"
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AudioControls;
